import React, { useState } from 'react'
import { Modal, Input, DatePicker } from 'antd'
const { RangePicker } = DatePicker
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { sendSalesOrPurchaseData } from 'src/features/WeeklySalesSlice'
import dayjs from 'dayjs'

const MisCostModal = ({
  visibleMiscCostModal,
  setVisibleMiscCostModal,
  dateRange,
}) => {
  const [date, setDate] = useState(null) // Can be single date or [start, end]
  const [purcheses, setPurchases] = useState(0)
  const [text, setText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const venueId = useSelector(state => state.venue?.venue?._id)

  const resetForm = () => {
    setDate(null)
    setPurchases(0)
    setText('')
  }

  const onChangeDate = dates => {
    if (dates && dates.length === 2) {
      // Range selected
      setDate(dates)
    } else if (dates && dates.length === 1) {
      // Single date selected (fallback)
      setDate([dates[0], dates[0]])
    } else {
      setDate(null)
    }
  }

  return (
    <Modal
      width={'40%'}
      title={'Add Misc Costs'}
      okText={'Save'}
      visible={visibleMiscCostModal}
      confirmLoading={isSubmitting}
      okButtonProps={{ disabled: isSubmitting }}
      onOk={async () => {
        if (!date) {
          // Validation: date must be selected
          return
        }

        // Prevent double-clicks
        if (isSubmitting) {
          return
        }

        setIsSubmitting(true)

        try {
          const totalAmount = parseFloat(purcheses) || 0
          const purchaseText = text || ''

          // Check if it's a single date or range
          const isRange =
            Array.isArray(date) &&
            date.length === 2 &&
            !date[0].isSame(date[1], 'day')

          if (isRange) {
            // Date range: split the cost across all days
            const startDate = date[0]
            const endDate = date[1]
            const days = []
            let currentDate = startDate.clone()

            while (currentDate.isSameOrBefore(endDate, 'day')) {
              days.push(currentDate.clone())
              currentDate = currentDate.add(1, 'day')
            }

            const numDays = days.length

            // Validate max days limit (365)
            if (numDays > 365) {
              console.error('Date range exceeds maximum of 365 days')
              // Don't close modal, show error to user
              alert(
                'Date range cannot exceed 365 days. Please select a smaller range.',
              )
              return
            }

            const amountPerDay = totalAmount / numDays
            const roundedAmount = Math.round(amountPerDay * 100) / 100 // Round to 2 decimal places

            // Calculate remainder to apply to last day
            const totalRounded = roundedAmount * (numDays - 1)
            const lastDayAmount = totalAmount - totalRounded

            // Build bulkPurchases array
            const bulkPurchases = []
            for (let i = 0; i < days.length; i++) {
              bulkPurchases.push({
                date: days[i].format('YYYY-MM-DD'),
                purchase: {
                  text: purchaseText,
                  amount: i === days.length - 1 ? lastDayAmount : roundedAmount,
                },
              })
            }

            // Prepare body with bulkPurchases array and send in single API call
            const body = {
              bulkPurchases: bulkPurchases,
            }

            // Send single API call with bulkPurchases array
            try {
              await dispatch(sendSalesOrPurchaseData(venueId, body, dateRange))
              // If successful, close modal
              resetForm()
              setVisibleMiscCostModal(false)
            } catch (bulkError) {
              // Handle bulk operation errors (including partial success)
              console.error('Error saving bulk misc costs:', bulkError)

              // Check if it's a partial success response from backend
              const errorResponse =
                bulkError?.response?.data || bulkError?.data || bulkError

              if (
                errorResponse?.successful !== undefined &&
                errorResponse?.failed !== undefined
              ) {
                // Partial success: some dates succeeded, some failed
                const { successful, failed, errors } = errorResponse
                const errorDetails =
                  errors?.map(e => `- ${e.date}: ${e.error}`).join('\n') ||
                  'Unknown errors'

                alert(
                  `Partially saved: ${successful} dates succeeded, ${failed} dates failed.\n\n` +
                    `Failed dates:\n${errorDetails}\n\n` +
                    `Please check the table and retry failed dates if needed.`,
                )
                // Close modal on partial success so user can see results and retry if needed
                resetForm()
                setVisibleMiscCostModal(false)
              } else {
                // Complete failure or other error
                const errorMessage =
                  errorResponse?.message ||
                  errorResponse?.error ||
                  'Failed to save misc costs. Please try again.'
                alert(`Error: ${errorMessage}`)
                // Don't close modal on complete failure so user can retry
              }
            }
          } else {
            // Single date: apply the full cost to that day.
            // IMPORTANT: backend expects purchase to be an array.
            const singleDate =
              Array.isArray(date) && date.length > 0 ? date[0] : date
            const body = {
              date: dayjs(singleDate).format('YYYY-MM-DD'),
              purchase: [
                {
                  text: purchaseText,
                  amount: totalAmount,
                },
              ],
            }
            try {
              await dispatch(sendSalesOrPurchaseData(venueId, body, dateRange))
              resetForm()
              setVisibleMiscCostModal(false)
            } catch (singleError) {
              console.error('Error saving single misc cost:', singleError)
              const message =
                singleError?.response?.data?.message ||
                singleError?.response?.data ||
                singleError?.message ||
                'Failed to save misc costs. Please try again.'
              alert(message)
              // Don't close modal on error so user can retry
            }
          }
        } catch (error) {
          console.error('Error saving misc costs:', error)
          // Fallback error handling for unexpected errors
          alert('An unexpected error occurred. Please try again.')
        } finally {
          setIsSubmitting(false)
        }
      }}
      onCancel={() => {
        resetForm()
        setVisibleMiscCostModal(false)
      }}
    >
      <SalesWrapper>
        <RangePicker
          onChange={onChangeDate}
          value={date}
          style={{ width: '60%', marginBottom: '20px' }}
        />
        <HelperText>
          Select a single date to apply the full cost to that day.
          <br />
          Select a date range to automatically split the cost evenly across all
          days.
        </HelperText>
        <Input
          style={{ width: '60%', marginBottom: '20px' }}
          placeholder='Purchases Figure'
          value={purcheses}
          onChange={event => {
            setPurchases(event.target.value)
          }}
        />
        <Input
          style={{ width: '60%', marginBottom: '20px' }}
          placeholder='Wages,Utility Bills'
          value={text}
          onChange={event => {
            setText(event.target.value)
          }}
        />
      </SalesWrapper>
    </Modal>
  )
}
const SalesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
`

const HelperText = styled.p`
  width: 60%;
  font-size: 13px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border-left: 3px solid #b5d922;
`

export default MisCostModal
